import { create } from 'zustand'
import type { Toast, SidebarTab } from '@/types'

export type AppPage = 'main' | 'translations' | 'editor' | 'dictionary'

interface UIState {
  // Page navigation
  currentPage: AppPage
  setCurrentPage: (page: AppPage) => void

  // Sidebar
  sidebarCollapsed: boolean
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleSidebar: () => void
  activeTab: SidebarTab
  setActiveTab: (tab: SidebarTab) => void

  // Mobile
  isMobile: boolean
  setIsMobile: (isMobile: boolean) => void

  // Advanced options
  showAdvancedOptions: boolean
  setShowAdvancedOptions: (show: boolean) => void
  toggleAdvancedOptions: () => void

  // API Status
  apiConnected: boolean
  setApiConnected: (connected: boolean) => void

  // Toasts
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
  clearToasts: () => void
}

let toastId = 0

export const useUIStore = create<UIState>((set) => ({
  // Page navigation
  currentPage: 'main',
  setCurrentPage: (page) => set({ currentPage: page }),

  // Sidebar
  sidebarCollapsed: false,
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
  toggleSidebar: () =>
    set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  activeTab: 'search',
  setActiveTab: (tab) => set({ activeTab: tab }),

  // Mobile
  isMobile: false,
  setIsMobile: (isMobile) =>
    set((state) => ({
      isMobile,
      // Auto-collapse sidebar on mobile
      sidebarCollapsed: isMobile ? true : state.sidebarCollapsed
    })),

  // Advanced options
  showAdvancedOptions: false,
  setShowAdvancedOptions: (show) => set({ showAdvancedOptions: show }),
  toggleAdvancedOptions: () =>
    set((state) => ({ showAdvancedOptions: !state.showAdvancedOptions })),

  // API Status
  apiConnected: false,
  setApiConnected: (connected) => set({ apiConnected: connected }),

  // Toasts
  toasts: [],
  addToast: (toast) => {
    const id = `toast-${++toastId}`
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? 5000
    }

    set((state) => ({
      toasts: [...state.toasts, newToast]
    }))

    // Auto-remove after duration
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id)
        }))
      }, newToast.duration)
    }

    return id
  },
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id)
    })),
  clearToasts: () => set({ toasts: [] })
}))
